import { gridDateComparator, gridNumberComparator } from '@mui/x-data-grid-pro';
import { CuriosityNamespaceType } from '@procureai/curiosity-ui-mui';
import { DateFormatPreset } from '@procureai/ui-mui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  QuoteLineItemGridColumnGroup,
  QuoteLineItemGridField,
  QuoteLineItemGridRow,
} from '../../NegotiationQuoteDetailsWidget.types';
import { useQuoteContext } from '../QuoteContextProvider';
import { TsrctQuoteItemUpdatableStatus } from '@/generated/gql/graphql';
import { I18nNamespace } from '~/common/types';
import { useConfig } from '~/components/ConfigProvider';
import {
  cellFactory,
  CurrencyCell,
  DataGridConfig,
  DateEditCell,
  dateFormatterFactory,
  editCellFactory,
  EmptyFallbackCell,
  GridAlignmentType,
  gridColDefFactory,
  gridColumnGroupFactory,
  headerGroupWithIcon,
  NegotiationCaseQuoteStatusCell,
  UseDataEditGridConfig,
} from '~/components/DataGridCustom';
import { CuriosityValueCell } from '~/components/DataGridCustom/cells/CuriosityValueCell/CuriosityValueCell';
import { DecimalTextFieldCell } from '~/components/DataGridCustom/editCells/DecimalTextFieldCell/DecimalTextFieldCell';
import { TextAreaCell } from '~/components/DataGridCustom/editCells/TextAreaCell/TextAreaCell';
import { IconId } from '~/components/Icon';

const pathPrefix = ['quote', 'lineItems'];

export type QuoteEditDataGridConfigParams = Parameters<UseDataEditGridConfig<QuoteLineItemGridRow>>[0] & {
  onStatusChange?: (rowId: string | number, newStatus: TsrctQuoteItemUpdatableStatus) => void;
};

export const useQuoteEditDataGridConfig = (config: QuoteEditDataGridConfigParams) => {
  const { getRowIndex, onStatusChange } = config;
  const { t } = useTranslation();
  const { t: tColumn } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'QuoteLineItemDataGrid.column' });
  const { t: tGroup } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'QuoteLineItemDataGrid.group' });
  const { isMasked } = useQuoteContext();
  const { features } = useConfig();
  return useMemo<DataGridConfig<QuoteLineItemGridRow>>(() => {
    const standardCellConfig = {
      pathPrefix,
      getRowIndex,
      languageNamespace: I18nNamespace.widgets,
      keyPrefix: 'QuoteLineItemDataGrid.column',
    };

    const createColumn = gridColDefFactory({ tColumn, t, defaultOptions: { sortable: false } });
    const createGroup = gridColumnGroupFactory({ tGroup });

    return {
      sortModel: [],
      columnVisibilityModel: {
        [QuoteLineItemGridField.STATUS]: features.enableNegotiationQuoteLineItemBidding,
      },
      columns: [
        createColumn({
          field: QuoteLineItemGridField.POSITION,
          minWidth: 100,
          hideable: false,
          resizable: false,
          renderCell: cellFactory(EmptyFallbackCell, standardCellConfig),
        }),
        createColumn({
          field: QuoteLineItemGridField.TITLE,
          minWidth: 300,
          renderCell: cellFactory(EmptyFallbackCell, standardCellConfig),
        }),
        createColumn({
          field: QuoteLineItemGridField.DESCRIPTION,
          minWidth: 300,
          renderCell: editCellFactory(TextAreaCell, standardCellConfig),
        }),
        createColumn({
          field: QuoteLineItemGridField.UNIT,
          minWidth: 150,
          renderCell: cellFactory(CuriosityValueCell, {
            ...standardCellConfig,
            ns: CuriosityNamespaceType.UNIT,
          }),
        }),
        createColumn({
          field: QuoteLineItemGridField.QUANTITY,
          minWidth: 150,
          sortComparator: gridNumberComparator,
          valueGetter: (_value, row) => {
            return row.quantity;
          },
          renderCell: editCellFactory(DecimalTextFieldCell, {
            ...standardCellConfig,
            decimalScale: 2,
          }),
        }),
        createColumn({
          field: QuoteLineItemGridField.PRICE_PER_UNIT,
          minWidth: 150,
          renderCell: (props) => {
            return cellFactory(CurrencyCell, ({ row }) => ({ isMasked, value: row.pricePerUnit }))(props);
          },
        }),
        createColumn({
          field: QuoteLineItemGridField.FROM,
          valueGetter: (_value, row) => row.shipmentTerms?.earlyDeliveryAt,
          valueFormatter: dateFormatterFactory({ preset: DateFormatPreset.TWO_DIGIT }),
          sortComparator: gridDateComparator,
          minWidth: 200,
          renderCell: editCellFactory(DateEditCell, standardCellConfig),
        }),
        createColumn({
          field: QuoteLineItemGridField.TO,
          valueGetter: (_value, row) => row.shipmentTerms?.lateDeliveryAt,
          valueFormatter: dateFormatterFactory({ preset: DateFormatPreset.TWO_DIGIT }),
          sortComparator: gridDateComparator,
          minWidth: 200,
          renderCell: editCellFactory(DateEditCell, standardCellConfig),
        }),
        createColumn({
          field: QuoteLineItemGridField.STATUS,
          minWidth: 150,
          editable: true,
          type: 'boolean',
          renderCell: cellFactory(NegotiationCaseQuoteStatusCell, ({ api }) => ({
            viewOnly: false,
            onStatusToggle: (rowId: string | number, newStatus: TsrctQuoteItemUpdatableStatus) => {
              onStatusChange?.(rowId, newStatus);
              api.updateRows([{ id: rowId, status: newStatus }]);
            },
          })),
        }),
      ].filter(Boolean),
      columnGroupingModel: [
        createGroup({
          groupId: QuoteLineItemGridColumnGroup.QUOTE,
          children: [
            //
            { field: QuoteLineItemGridField.POSITION },
            { field: QuoteLineItemGridField.TITLE },
            { field: QuoteLineItemGridField.DESCRIPTION },
            { field: QuoteLineItemGridField.UNIT },
            { field: QuoteLineItemGridField.QUANTITY },
            { field: QuoteLineItemGridField.PRICE_PER_UNIT },
          ],
        }),
        createGroup({
          groupId: QuoteLineItemGridColumnGroup.DELIVERY_DATE,
          renderHeaderGroup: headerGroupWithIcon(IconId.deliveryDate),
          children: [
            //
            { field: QuoteLineItemGridField.FROM },
            { field: QuoteLineItemGridField.TO },
          ],
        }),
        createGroup({
          groupId: QuoteLineItemGridColumnGroup.ACTIONS,
          headerAlign: GridAlignmentType.CENTER,
          children: [{ field: QuoteLineItemGridField.STATUS }],
        }),
      ],
    };
  }, [isMasked, getRowIndex, onStatusChange, t, tColumn, tGroup, features.enableNegotiationQuoteLineItemBidding]);
};
